import { treaty, type Treaty } from '@elysiajs/eden'
import { createIsomorphicFn } from '@tanstack/react-start'
import { getRequestClientIp, getRequestCookie, getRequestOrigin } from './request-context'
import { redactUrlForLog } from './redact-url-for-log'

/**
 * Shared fetch wrapper for SSR — single instance reused by all treaty clients.
 * Prevents connection leak: Bun's fetch uses HTTP keep-alive, so each treaty()
 * call that creates a new fetcher opens connections that accumulate.
 * With a single fetcher, Bun reuses the same connection pool.
 */
// ⚠️ Não ANOTAR `: typeof fetch` — o `fetch` do Bun carrega `preconnect`, que
// uma função nossa nunca terá; a anotação reprovava o wrapper inteiro. O `as
// typeof fetch` no fim é o mesmo de `@buu/api-client`: o `treaty` pede
// `fetcher?: typeof fetch`, e o wrapper só é chamado, nunca `preconnect`ado.
const fetchWithLog = (async (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
  const reqUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  const start = Date.now()
  try {
    const res = await fetch(input, { ...init, signal: init?.signal ?? AbortSignal.timeout(10_000) })
    const ms = Date.now() - start
    if (ms > 2000) {
      console.warn(`[API SLOW] ${redactUrlForLog(reqUrl)} took ${ms}ms`)
    }
    if (!res.ok) {
      console.warn(`[API ${res.status}] ${redactUrlForLog(reqUrl)}`)
    }
    return res
  } catch (err) {
    const ms = Date.now() - start
    console.error(`[API FAIL] ${redactUrlForLog(reqUrl)} after ${ms}ms:`, err)
    throw err
  }
}) as typeof fetch

/**
 * O `Elysia` que o `treaty` exige — tirado da PRÓPRIA assinatura do `treaty`,
 * e não de um `import type { Elysia } from 'elysia'`: a restrição é sempre a da
 * cópia de `elysia` que o Eden instalado enxerga. Mesmo desenho de
 * `packages/api-client/src/api-factory.ts` (dívida D1, 30/09/2026).
 */
type AnyElysia = Exclude<Parameters<typeof treaty>[0], string>

/** O cliente Eden tipado de um conjunto de rotas da api. */
export type GranularTreaty<T extends AnyElysia> = Treaty.Create<T>

/**
 * Server-side treaty client cache.
 * Each unique config (withCookies vs not) gets one cached client,
 * preventing 256+ keep-alive connections from accumulating.
 */
const serverClientCache = new Map<string, unknown>()

/**
 * Factory to create granular Eden Treaty clients.
 *
 * - Server (SSR): calls API directly via internal URL, forwarding cookies
 *   and the original Origin header so the API can resolve the workspace
 *   from the domain.
 *   Treaty client is cached per config — all SSR requests share the same
 *   HTTP connections instead of opening new ones.
 * - Client (browser): calls same origin — Vite proxy (dev) / reverse proxy (prod)
 *   forwards /api/* to the API server. Same-origin = no CORS, cookies automatic.
 *
 * ⚠️ Tipagem (dívida D1, 30/09/2026): até aqui `T` não tinha restrição e cada
 * `treaty<T>` levava um `@ts-ignore` ("a api tem outra cópia de elysia"). Um
 * `treaty<T>` fora da restrição é instanciação com ERRO, e o TS resolve erro
 * como `any`: as 14 fábricas do aluno devolviam `any` e o typecheck não
 * conferia resposta nenhuma. Hoje o `bun.lock` tem UMA cópia de `elysia` (a da
 * raiz). Se a segunda voltar, o sintoma é erro de tipo aqui — o remédio é
 * apagar a cópia órfã e reinstalar, nunca o `@ts-ignore` de volta.
 */
export function createGranularTreaty<T extends AnyElysia>(options?: {
  withCookies?: boolean
}): () => GranularTreaty<T> {
  return createIsomorphicFn()
    .server((): GranularTreaty<T> => {
      const cacheKey = options?.withCookies ? 'cookies' : 'no-cookies'
      const cached = serverClientCache.get(cacheKey)
      // O cache é por `withCookies`, não por `T`: o cliente Eden é um proxy que
      // monta o caminho na chamada — o tipo é só a lente de quem pediu.
      if (cached) return cached as GranularTreaty<T>

      const url = process.env.INTERNAL_API_URL || 'http://localhost:3002'

      const client = options?.withCookies
        ? treaty<T>(url, {
            fetcher: fetchWithLog,
            headers: () => {
              const cookie = getRequestCookie()
              const origin = getRequestOrigin()
              // Repassa o IP do VISITANTE. A api usa isto para rate limit; sem o
              // cabeçalho ela cai em 'unknown' e o teto vira global por tenant.
              const clientIp = getRequestClientIp()
              return {
                ...(cookie ? { cookie } : {}),
                ...(origin ? { origin } : {}),
                ...(clientIp ? { 'x-forwarded-for': clientIp } : {}),
              }
            },
          })
        : treaty<T>(url, { fetcher: fetchWithLog })

      serverClientCache.set(cacheKey, client)
      return client
    })
    .client((): GranularTreaty<T> => {
      // Same origin — /api/* proxied to API server (no CORS, cookies automatic)
      const clientFetch = (async (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
        const res = await fetch(input, init)
        if (!res.ok) {
          const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
          console.warn(`[API ${res.status}] ${redactUrlForLog(url)}`)
        }
        return res
      }) as typeof fetch
      return treaty<T>(window.location.origin, { fetcher: clientFetch })
    })
}
